import {JsonObject} from "@/shared/json/json-object";
import {JsonProperty} from "@/shared/json/json-property";
import {JSON_OBJECT, Type} from "@/shared/json/helpers";
import {
  $KTM,
  $KTS,
  BaseListItem,
  BaseListItemsLoader,
  ColorValue,
  DisplayName,
  ENV,
  KeyMetadatas,
  KeyTextString,
  KeyTextStrings,
  LoadListOptions,
  Notification,
  Notifications,
  ObjectConfig,
  OnListItemsListener,
  TypedObject
} from "@/shared/types";
import {ContextTrait} from "../shared/BaseApp";
import {md5, md5_code, md5_uuid} from "@/shared/md5";
import {ProvisioningContext, Rel} from "../shared/database";
import {FormGen} from "../shared/formgen";
import {
  ArticleOutlined,
  BusinessOutlined,
  FolderOutlined,
  HistoryEduOutlined,
  NewspaperOutlined,
  SchoolOutlined
} from "@mui/icons-material";
import {SvgIcon} from "@mui/material";
import {StringUtil} from "../common/string_util";
import {DirectoryOrgUnitRoot} from "../shared/directory/types";
import {Environment} from "@/common/types";
import {black, colorPastelBlue, colorPastelGreen, colorPastelRed, colorPastelYellow, gray} from "../shared/colors";
import {getAuth} from "@firebase/auth";

export function getServiceHostForEnvironment(env: Environment): string {
  switch (env) {
    case Environment.DEBUG:
    case Environment.EMULATOR_DEBUG:
      return "http://localhost:8080";
    case Environment.EMULATOR_LOCAL:
      return `http://127.0.0.1:5001/praxis-6b6a8/us-central1/fyneai`;
    case Environment.EMULATOR_TEST:
    case Environment.TEST:
      throw new Error("Not setup yet.");
    default:
    case Environment.STAGING: // Assumes db is staging (e.g. praxis-6b6a8) but services are prod. Useful for DB dev.
    case Environment.PROD:
      return `https://fyneai-apps-fyneai-service.web.app`;
  }
}

export function getServiceUrlForEnvironment(env: Environment, path: string): string {
  return `${getServiceHostForEnvironment(env)}/${path}`;
}

export function getServiceUrl(path: string): string {
  return `${getServiceHostForEnvironment(ENV)}/${path}`;
}

export function getLabelerTeamMemberInviteUrl(): string {
  return getServiceUrl(`labeler/teamMembers/invite`);
}

export function getLabelerImportProcessUrl(): string {
  return getServiceUrl(`labeler/imports/process`);
}

export function getMyLabelerTeamMemberId(): string {
  return md5(getAuth().currentUser.email);
}

export interface NotificationsContextTrait extends ContextTrait {

  getNotificationsSource(): NotificationsSource;
}

export interface NotificationsSource {

  registerObserver(observer: OnListItemsListener<Notification>): void;

  unregisterObserver(observer: OnListItemsListener<Notification>): void;

  getNotificationsCount(): number;

  showPopup(anchorEl: HTMLElement): void;
}


export enum ResourceAccessRole {
  ADMIN = "admin",
  LABELER = "labeler",
}

export interface ResourceContextTrait extends ContextTrait {

  getOrganizationResource(): Resource;
}

export enum ResourceType {
  ORGANIZATION = "organization",
  FOLDER = "folder",
  PROJECT = "project",
}

export type ResourceTypeMetadata = {
  text: string,
  iconType: typeof SvgIcon,
}

export const RESOURCE_TYPE_KTM = new KeyMetadatas<ResourceTypeMetadata>([
  $KTM(ResourceType.ORGANIZATION, {text: "Organization", iconType: BusinessOutlined}),
  $KTM(ResourceType.FOLDER, {text: "Folder", iconType: FolderOutlined}),
  $KTM(ResourceType.PROJECT, {text: "Project", iconType: ArticleOutlined}),
]);

export type ModuleConfig = {
  requiresResourceType?: ResourceType,
}

@JsonObject()
export class ResourceNode extends BaseListItem {

  @JsonProperty()
  readonly type: ResourceType;

  project?: Project; // Set only when type === PageTypeName.PROJECT

  folder?: Folder; // Set only when type === PageTypeName.FOLDER

  organization?: Organization; // Set only when type === PageTypeName.PROVIDER

  @JsonProperty()
  _resource: any;

  getResource(): Resource {
    switch (this.type) {
      case ResourceType.ORGANIZATION:
        return this.organization;
      case ResourceType.FOLDER:
        return this.folder;
      case ResourceType.PROJECT:
        return this.project;
    }
    return null;
  }

  async onBeforeItemSerialized(): Promise<void> {
    await super.onBeforeItemSerialized();
    switch (this.type) {
      case ResourceType.PROJECT: {
        this._resource = JSON_OBJECT.serializeObject(this.project);
        break;
      }
      case ResourceType.FOLDER: {
        this._resource = JSON_OBJECT.serializeObject(this.folder);
        break;
      }
      case ResourceType.ORGANIZATION: {
        this._resource = JSON_OBJECT.serializeObject(this.organization);
        break;
      }
    }
  }

  // We should not assume Firebase is present in this method, as this object could be created by a non-app layout site resource (using API calls).
  async onAfterItemDeserialized(): Promise<void> {
    await super.onAfterItemDeserialized();
    switch (this.type) {
      case ResourceType.PROJECT: {
        const project = JSON_OBJECT.deserializeObject(this._resource, Project);
        project.setResourceNode(this);
        project.setParentResourceNode(await ResourceNodes.getInstance().getOrLoadItem(project.parentId));
        project.setOrganizationResourceNode(await ResourceNodes.getInstance().getOrLoadItem(project.organizationId));
        this.project = project;
        break;
      }
      case ResourceType.FOLDER: {
        const folder = JSON_OBJECT.deserializeObject(this._resource, Folder);
        folder.setResourceNode(this);
        folder.setParentResourceNode(await ResourceNodes.getInstance().getOrLoadItem(folder.parentId));
        folder.setOrganizationResourceNode(await ResourceNodes.getInstance().getOrLoadItem(folder.organizationId));
        this.folder = folder;
        break;
      }
      case ResourceType.ORGANIZATION: {
        const profile = JSON_OBJECT.deserializeObject(this._resource, Organization);
        profile.setResourceNode(this);
        profile.setParentResourceNode(this);
        profile.setOrganizationResourceNode(this);
        this.organization = profile;
        break;
      }
    }
  }

  static createNewOrganizationResource(root: DirectoryOrgUnitRoot): ResourceNode {
    const node = this.createResourceNode(ResourceType.ORGANIZATION);
    const res = new Organization(root.name, node.id, node.id);
    res.setResourceNode(node);
    node.organization = res;
    return node;
  }

  static createNewFolderResource(name: string, parentId: string, organizationId: string): ResourceNode {
    const node = this.createResourceNode(ResourceType.FOLDER);
    const res = new Folder(name, parentId, organizationId);
    res.setResourceNode(node);
    node.folder = res;
    return node;
  }

  static createNewProjectResource(name: string, parentId: string, organizationId: string): ResourceNode {
    const node = this.createResourceNode(ResourceType.PROJECT);
    const res = new Project(name, parentId, organizationId);
    res.setResourceNode(node);
    node.project = res;
    return node;
  }

  static fromOrganization(organizationProfile: Organization): ResourceNode {
    const resource = this.createResourceNode(ResourceType.ORGANIZATION);
    organizationProfile.setResourceNode(resource);
    resource.organization = organizationProfile;
    return resource;
  }

  static fromProject(projectProfile: Project): ResourceNode {
    const resource = this.createResourceNode(ResourceType.PROJECT);
    projectProfile.setResourceNode(resource);
    resource.project = projectProfile;
    return resource;
  }

  private static createResourceNode(type: ResourceType): ResourceNode {
    return new ResourceNode(md5_uuid(), type);
  }

  static async fromJson(json: any): Promise<ResourceNode> {
    const resource = JSON_OBJECT.deserializeObject(json, ResourceNode);
    await resource?.onAfterItemDeserialized();
    return resource;
  }

  constructor(id: string, type: ResourceType) {
    super(id);
    this.type = type;
  }
}

export class ResourceNodes extends BaseListItemsLoader<ResourceNode> {

  private static instance: ResourceNodes;

  static getInstance(role?: ResourceAccessRole): ResourceNodes {
    if (!this.instance) {
      this.instance = new ResourceNodes(role);
    }
    return this.instance;
  }

  private constructor(private readonly role: ResourceAccessRole) {
    super({shared: true});
  }

  protected async fetchListItems(options?: LoadListOptions): Promise<ResourceNode[]> {
    let nodeItems = await super.fetchListItems(options);
    switch (this.role) {
      case ResourceAccessRole.LABELER: {
        const labelerNodeItems: ResourceNode[] = [];
        for (const nodeItem of nodeItems) {
          if (!(nodeItem.type === ResourceType.ORGANIZATION || nodeItem.type === ResourceType.PROJECT)) {
            continue;
          }
          if (nodeItem.type === ResourceType.ORGANIZATION) {
            labelerNodeItems.push(nodeItem);
          } else if (nodeItem.type === ResourceType.PROJECT) {
            if (await new LabelerTeamMembers(nodeItem.id).getOrLoadItem(getMyLabelerTeamMemberId())) {
              labelerNodeItems.push(nodeItem);
            }
          }
        }
        nodeItems = labelerNodeItems;
      }
        break;
    }
    return nodeItems;
  }

  protected basePath(): string {
    return "resources";
  }

  protected deserializeItem(value: any): ResourceNode {
    return JSON_OBJECT.deserializeObject(value, ResourceNode);
  }

  protected serializeItem(item: ResourceNode): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: ResourceNode, item2: ResourceNode): number {
    return item1.createdAt - item2.createdAt;
  }
}

export type ResourceFilter = {
  includeTypes?: ResourceType[],
  excludeTypes?: ResourceType[]
}

export function ResourceNodesFiltered(nodes: ResourceNode[], filter: ResourceFilter): ResourceNode[] {
  if (!nodes) {
    return [];
  }
  let filtered = [...nodes];
  if (filter?.includeTypes) {
    filtered = filtered.filter(node => filter.includeTypes.includes(node.type));
  }
  if (filter?.excludeTypes) {
    filtered = filtered.filter(node => !filter.excludeTypes.includes(node.type));
  }
  return filtered;
}

export function ResourcesFiltered(resources: Resource[], filter: ResourceFilter): Resource[] {
  if (!resources) {
    return [];
  }
  let filtered = [...resources];
  if (filter?.includeTypes) {
    filtered = filtered.filter(res => filter.includeTypes.includes(res.getType()));
  }
  if (filter?.excludeTypes) {
    filtered = filtered.filter(res => !filter.excludeTypes.includes(res.getType()));
  }
  return filtered;
}

export function resourceAutocompleteLoaderCreator(filter?: ResourceFilter): () => Promise<KeyTextString[]> {
  return () => {
    const resNodes = ResourceNodes.getInstance();
    return (async function () {
      const nodes = await resNodes.getOrLoadListItems();
      return ResourceNodesFiltered(nodes, filter).map(node => $KTS(node.id, node.getResource().name));
    })();
  };
}

@JsonObject()
export abstract class Resource {

  private _organization: ResourceNode; // Not serialized.
  private _parent: ResourceNode; // Not serialized.
  private _node: ResourceNode; // Not serialized.

  @JsonProperty()
  readonly name: string;

  @JsonProperty()
  readonly parentId: string;

  @JsonProperty()
  readonly organizationId: string;

  constructor(name: string, parentId: string, organizationId: string) {
    this.name = name;
    this.parentId = parentId;
    this.organizationId = organizationId;
  }

  getType(): ResourceType {
    return this._node.type;
  }

  getOrganizationResourceNode(): ResourceNode {
    return this._organization;
  }

  setOrganizationResourceNode(node: ResourceNode): void {
    this._organization = node;
  }

  getParentResourceNode(): ResourceNode {
    return this._parent;
  }

  setParentResourceNode(node: ResourceNode): void {
    this._parent = node;
  }

  getResourceNode(): ResourceNode {
    return this._node;
  }

  setResourceNode(node: ResourceNode): void {
    this._node = node;
  }
}

@JsonObject()
export class Project extends Resource {

}

@JsonObject()
export class Folder extends Resource {

}

@JsonObject()
export class Organization extends Resource {

}

////////// Resource Scoped Objects /////////////////

export class ResourceData {

  private static resource: Resource;
  private static projectInstance: ResourceDataProject;
  private static organizationInstance: ResourceDataOrganization;

  // This must be called whenever current Project selection changes.
  static async createAndInitProject(resource: Resource): Promise<void> {
    this.resource = resource;
    if (this.resource.getType() !== ResourceType.PROJECT) {
      throw new Error("Incorrect resource type.");
    }
    const projectId = this.resource.getResourceNode().id;
    this.projectInstance = new ResourceDataProject(
      resource,
      Notifications.getSharedInstance(),
      new ResourceDataProjectModuleLabeler(
        new LabelerDatasets(projectId),
        new LabelerTeamMembers(projectId),
        new LabelerAssignments(projectId),
      ),
    );
    await this.projectInstance.init();
  }

  // This must be called whenever current Organization selection changes.
  static async createAndInitOrganization(resource: Resource): Promise<void> {
    this.resource = resource;
    if (this.resource.getType() !== ResourceType.ORGANIZATION) {
      throw new Error("Incorrect resource type.");
    }
    const organizationId = this.resource.getResourceNode().id;
    this.organizationInstance = new ResourceDataOrganization(
      resource,
      Notifications.getSharedInstance(),
    );
    await this.organizationInstance.init();
  }

  static shared(): ResourceDataShared {
    switch (this.resource.getType()) {
      case ResourceType.PROJECT:
        return this.projectInstance;
      case ResourceType.ORGANIZATION:
        return this.organizationInstance;
      case ResourceType.FOLDER:
        throw new Error("Illegal operation.");
    }
    return null;
  }

  static project(): ResourceDataProject {
    return this.projectInstance;
  }

  static organization(): ResourceDataOrganization {
    return this.organizationInstance;
  }

  static projectId(): string {
    return this.projectInstance?.resource.getResourceNode().id;
  }

  static organizationId(): string {
    return this.shared()?.resource.getOrganizationResourceNode().id;
  }
}

export abstract class ResourceDataShared {

  constructor(
    readonly resource: Resource,
    readonly notifications: Notifications,
  ) {
  }

  async init(): Promise<void> {
  }
}

export class ResourceDataProject extends ResourceDataShared {

  constructor(
    resource: Resource,
    notifications: Notifications,
    readonly labeler: ResourceDataProjectModuleLabeler,
  ) {
    super(resource, notifications);
  }

  async init(): Promise<void> {
    await super.init();
    await this.labeler.init();
  }
}

export class ResourceDataProjectModuleLabeler {

  constructor(
    readonly datasets: LabelerDatasets,
    readonly teamMembers: LabelerTeamMembers,
    readonly assignments: LabelerAssignments,
  ) {
  }

  async init(): Promise<void> {
  }
}

export class ResourceDataOrganization extends ResourceDataShared {

  constructor(
    resource: Resource,
    notifications: Notifications,
  ) {
    super(resource, notifications);
  }
}

export abstract class BaseProjectTypedObject extends TypedObject {

  constructor(readonly projectId: string) {
    super(null, null);
  }

  protected objectConfig(): ObjectConfig | null {
    return {
      shared: true,
    };
  }

  protected basePath(): string | null {
    return `projectData/${this.projectId}`;
  }
}

@JsonObject()
export abstract class BaseProjectListItem extends BaseListItem {

  constructor(id: string) {
    super(id);
  }
}

export abstract class BaseProjectListItemsLoader<T extends BaseProjectListItem> extends BaseListItemsLoader<T> {

  constructor(readonly projectId: string) {
    super({shared: true});
  }

  protected basePath(): string {
    return `projectData/${this.projectId}/${this.projectDataBasePath()}`;
  }

  protected abstract projectDataBasePath(): string;
}

/// Labeler

export enum LabelerDatasetDataType {
  IMAGE = "image",
  DICOM = "dicom",
  TEXT = "text",
  HTML = "html",
  VIDEO = "video",
  PDF = "pdf",
}

export const LABELER_DATASET_DATA_TYPE_KTS = new KeyTextStrings([
  $KTS(LabelerDatasetDataType.IMAGE, "Image"),
  $KTS(LabelerDatasetDataType.DICOM, "DICOM"),
  $KTS(LabelerDatasetDataType.TEXT, "Text"),
  $KTS(LabelerDatasetDataType.HTML, "HTML"),
  $KTS(LabelerDatasetDataType.VIDEO, "Video"),
  $KTS(LabelerDatasetDataType.PDF, "PDF"),
]);

@JsonObject()
export class LabelerDataset extends BaseProjectListItem {

  @FormGen({
    name: "Name",
    type: "string",
    required: true,
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Data type",
    type: "enum",
    required: true,
    enumValues: LABELER_DATASET_DATA_TYPE_KTS.values,
  })
  @JsonProperty()
  dataType: LabelerDatasetDataType;

  @FormGen({
    name: "Project description",
    type: "text",
    size: "xsmall",
    description: "Describe what this project is about (Optional).",
  })
  @JsonProperty()
  description: string;

  static createNew(): LabelerDataset {
    return new LabelerDataset(md5_uuid());
  }
}

export class LabelerDatasets extends BaseProjectListItemsLoader<LabelerDataset> {

  private readonly assetsCache = new Map<string, LabelerAssets>();

  constructor(projectId: string) {
    super(projectId);
  }

  protected projectDataBasePath(): string {
    return "labeler/datasets";
  }

  getAssets(datasetId: string, importId: string) {
    const key = `${datasetId}/${importId}`;
    let assets = this.assetsCache.get(key);
    if (!assets) {
      assets = new LabelerAssets(this.projectId, datasetId, importId);
      this.assetsCache.set(key, assets);
    }
    return assets;
  }

  protected deserializeItem(value: any): LabelerDataset {
    return JSON_OBJECT.deserializeObject(value, LabelerDataset);
  }

  protected serializeItem(item: LabelerDataset): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: LabelerDataset, item2: LabelerDataset): number {
    return item2.createdAt - item1.createdAt;
  }
}


export enum LabelerTaskCategory {
  CLASSIFICATION = "classification",
  SEGMENTATION = "segmentation",
}

export const LABELER_TASK_CATEGORY_KTS = new KeyTextStrings([
  $KTS(LabelerTaskCategory.CLASSIFICATION, "Classification"),
  $KTS(LabelerTaskCategory.SEGMENTATION, "Segmentation"),
]);

export enum LabelerSegmentationType {
  POINT = "point",
  ELLIPSE = "ellipse",
  RECTANGLE = "rectangle",
  POLYGON = "polygon",
  LINE = "line",
}

export const LABELER_SEGMENTATION_TYPE_KTS = new KeyTextStrings([
  $KTS(LabelerSegmentationType.POINT, "Point"),
  $KTS(LabelerSegmentationType.RECTANGLE, "Rectangle"),
  $KTS(LabelerSegmentationType.ELLIPSE, "Ellipse"),
  $KTS(LabelerSegmentationType.POLYGON, "Polygon"),
  $KTS(LabelerSegmentationType.LINE, "Line"),
]);

@JsonObject()
export class LabelerSegmentation {

  @JsonProperty()
  readonly type: LabelerSegmentationType;

  @JsonProperty()
  readonly points: number[];

  constructor(type: LabelerSegmentationType, points: number[]) {
    this.type = type;
    this.points = points;
  }
}

@JsonObject()
export class LabelerTaskCaseLabel {

  @JsonProperty()
  readonly answerClasses: string[];

  @JsonProperty()
  readonly segmentation?: LabelerSegmentation;

  constructor(answerClasses: string[], segmentation: LabelerSegmentation) {
    this.answerClasses = answerClasses;
    this.segmentation = segmentation;
  }
}

export enum LabelerTaskCaseStatus {
  UNLABALED = "unlabeled",
  SUBMITTED = "submitted",
  ACCEPTED = "accepted",
  RETURNED = "returned",
  REJECTED = "rejected",
}

export type LabelerTaskCaseStatusMetadata = {
  text: string,
  color: ColorValue,
}

export const LABELER_TASK_CASE_STATUS_KTM = new KeyMetadatas<LabelerTaskCaseStatusMetadata>([
  $KTM(LabelerTaskCaseStatus.UNLABALED, {text: "Unlabeled", color: new ColorValue(black, gray)}),
  $KTM(LabelerTaskCaseStatus.SUBMITTED, {text: "Submitted", color: new ColorValue(black, colorPastelBlue)}),
  $KTM(LabelerTaskCaseStatus.RETURNED, {text: "Returned", color: new ColorValue(black, colorPastelYellow)}),
  $KTM(LabelerTaskCaseStatus.ACCEPTED, {text: "Accepted", color: new ColorValue(black, colorPastelGreen)}),
  $KTM(LabelerTaskCaseStatus.REJECTED, {text: "Rejected", color: new ColorValue(black, colorPastelRed)}),
]);

@JsonObject()
export class LabelerTaskCase extends BaseProjectTypedObject {

  readonly id: string = `labeler/cases/${this.datasetId}/${this.taskId}/${this.importId}/${this.assetId}`;

  asset: LabelerAsset;

  @JsonProperty({dataStructure: "array", type: LabelerTaskCaseLabel})
  readonly labels: LabelerTaskCaseLabel[] = [];

  @JsonProperty()
  status: LabelerTaskCaseStatus = LabelerTaskCaseStatus.UNLABALED;

  constructor(projectId: string, readonly datasetId: string, readonly taskId: string, readonly importId: string, readonly assetId: string) {
    super(projectId);
  }

  async load(forceReload?: boolean, overrideProvisioningContext?: ProvisioningContext): Promise<void> {
    if (!this.task) {
      throw new Error("Task must be set before loading case.");
    }
    if (this.task.id !== this.taskId) {
      throw new Error("Task id mismatch");
    }
    await super.load(forceReload, overrideProvisioningContext);
  }

  private task: LabelerTask;

  getTask(): LabelerTask {
    return this.task;
  }

  // Do not use. Used by LabelerTask when loading cases.
  _setTask(task: LabelerTask) {
    this.task = task;
  }

  async onAfterObjectDeserialized(): Promise<void> {
    if (this.assetId) {
      this.asset = await new LabelerAssets(this.projectId, this.datasetId, this.importId).loadListItem(this.assetId);
    }
  }

  protected getType(): Type<any> {
    return LabelerTaskCase;
  }
}

@JsonObject()
export class LabelerTask extends BaseProjectListItem {

  @FormGen({
    name: "Category",
    required: true,
    type: "enum",
    enumValues: LABELER_TASK_CATEGORY_KTS.values,
    enumVariant: "button",
  })
  @JsonProperty()
  category: LabelerTaskCategory;

  @FormGen({
    name: "Segmentation types",
    required: true,
    type: "enum",
    enumValues: LABELER_SEGMENTATION_TYPE_KTS.values,
    enumMulti: true,
    description: "Type of segmentation (applicable only when category is 'Segmentation').",
  })
  @JsonProperty()
  readonly segmentationTypes: string[] = [];

  @FormGen({
    name: "Name",
    required: true,
    type: "string",
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Task prompt",
    required: true,
    type: "string",
    description: "What prompt should we give to the labelers?",
  })
  @JsonProperty()
  prompt: string;

  @FormGen({
    name: "Answer classes",
    type: "array_string",
    required: true,
  })
  @JsonProperty()
  answerClasses: string[];

  @FormGen({
    name: "Can labelers select more than one answer?",
    type: "boolean",
    required: true,
  })
  @JsonProperty()
  multiple: boolean;

  @FormGen({
    name: "Require review? If false, submitted labels are auto-accepted.",
    type: "boolean",
    required: true,
  })
  @JsonProperty()
  requireReview: boolean;

  @FormGen({
    name: "Task description",
    type: "text",
    size: "xsmall",
    description: "What prompt should we give to the labelers?",
  })
  @JsonProperty()
  description: string;

  imports: LabelerImport[] = [];

  @JsonProperty()
  importIds: string[] = [];

  // Not serialized, loaded dynamically.
  readonly cases: LabelerTaskCase[] = [];

  // Not serialized, loaded dynamically.
  assignment: LabelerAssignment;

  // Not serialized, loaded dynamically.
  teamMember: LabelerTeamMember;

  static createNew(): LabelerTask {
    return new LabelerTask(md5_uuid());
  }

  // Create a new instance with common fields copied.
  duplicate(): LabelerTask {
    const duplicate = LabelerTask.createNew();
    duplicate.category = this.category;
    duplicate.segmentationTypes.push(...this.segmentationTypes);
    duplicate.prompt = this.prompt;
    duplicate.answerClasses = [...(this.answerClasses || [])]
    duplicate.multiple = this.multiple;
    duplicate.requireReview = this.requireReview;
    duplicate.description = this.description;
    return duplicate;
  }
}

export class LabelerTasks extends BaseProjectListItemsLoader<LabelerTask> {

  constructor(projectId: string, readonly datasetId: string) {
    super(projectId);
  }

  protected async onAfterItemDeserialized(item: LabelerTask, val?: any): Promise<LabelerTask> {
    await super.onAfterItemDeserialized(item, val);
    const imports = new LabelerImports(this.projectId, this.datasetId)
    for (const importId of item.importIds) {
      const import_ = await imports.getOrLoadItem(importId);
      if (!import_) {
        continue;
      }
      item.imports.push(import_);
      const loader = ResourceData.project().labeler.datasets.getAssets(this.datasetId, importId);
      const assets = await loader.getOrLoadListItems();
      if (assets) {
        try {
          for (const asset of assets) {
            const case_ = new LabelerTaskCase(this.projectId, this.datasetId, item.id, importId, asset.id);
            case_._setTask(item);
            // await case_.load(); // May not exist, but we still add it so that labels may be added to this case.
            item.cases.push(case_);
          }
        } catch (e) {
          console.error(e);
        }
      }
    }
    const assignments = ResourceData.project().labeler.assignments;
    const teamMembers = ResourceData.project().labeler.teamMembers;
    let assignment = await assignments.loadListItem(LabelerAssignment.genId(this.datasetId, item.id));
    if (assignment?.teamMemberId) {
      item.teamMember = await teamMembers.getOrLoadItem(assignment.teamMemberId);
    } else {
      assignment = LabelerAssignment.createNew(this.datasetId, item.id);
    }
    item.assignment = assignment;
    return item;
  }

  protected projectDataBasePath(): string {
    return `labeler/tasks/${this.datasetId}`;
  }

  protected deserializeItem(value: any): LabelerTask {
    return JSON_OBJECT.deserializeObject(value, LabelerTask);
  }

  protected serializeItem(item: LabelerTask): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: LabelerTask, item2: LabelerTask): number {
    return item2.createdAt - item1.createdAt;
  }
}

export enum LabelerImportStatus {
  PENDING = "pending",
  PROCESSING = "processing",
  READY = "ready",
  ERROR = "error",
}

export type LabelerImportStatusMetadata = {
  text: string,
  color: ColorValue,
}

export const LABELER_IMPORT_STATUS_KTM = new KeyMetadatas<LabelerImportStatusMetadata>([
  $KTM(LabelerImportStatus.PENDING, {text: "Pending", color: new ColorValue(black, gray)}),
  $KTM(LabelerImportStatus.PROCESSING, {text: "Processing", color: new ColorValue(black, colorPastelYellow)}),
  $KTM(LabelerImportStatus.READY, {text: "Ready", color: new ColorValue(black, colorPastelGreen)}),
  $KTM(LabelerImportStatus.ERROR, {text: "Error", color: new ColorValue(black, colorPastelRed)}),
]);

@JsonObject()
export class LabelerImport extends BaseProjectListItem {

  @FormGen({
    name: "Name",
    required: true,
    type: "string",
  })
  @JsonProperty()
  name: string;

  @FormGen({
    name: "Zip file",
    required: true,
    type: "file",
    fileMimeTypes: ["application/zip"],
    fileStoredDataSource: "labeler/imports",
  })
  @JsonProperty()
  fileUrl: string;

  @JsonProperty()
  status: LabelerImportStatus = LabelerImportStatus.PENDING;

  // Not serialized
  assets: LabelerAsset[];

  static createNew(): LabelerImport {
    return new LabelerImport(md5_uuid());
  }
}

export class LabelerImports extends BaseProjectListItemsLoader<LabelerImport> {

  constructor(projectId: string, readonly datasetId: string) {
    super(projectId);
  }

  protected projectDataBasePath(): string {
    return `labeler/imports/${this.datasetId}`;
  }

  protected async onAfterItemDeserialized(item: LabelerImport, val?: any): Promise<LabelerImport> {
    await super.onAfterItemDeserialized(item, val);
    const loader = ResourceData.project().labeler.datasets.getAssets(this.datasetId, item.id);
    item.assets = await loader.getOrLoadListItems();
    return item;
  }

  protected deserializeItem(value: any): LabelerImport {
    return JSON_OBJECT.deserializeObject(value, LabelerImport);
  }

  protected serializeItem(item: LabelerImport): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: LabelerImport, item2: LabelerImport): number {
    return item2.createdAt - item1.createdAt;
  }

  async deleteListItem(item: LabelerImport, ...rels): Promise<void> {
    await (new LabelerAssets(this.projectId, this.datasetId, item.id)).deleteAllListItems();
    return super.deleteListItem(item, ...rels);
  }
}

@JsonObject()
export class LabelerAsset extends BaseProjectListItem {

  @JsonProperty()
  readonly fileUrl: string;

  constructor(id: string, fileUrl: string) {
    super(id);
    this.fileUrl = fileUrl;
  }

  getSortIndex(): number {
    return md5_code(this.id);
  }

  getFiles(): string[] {
    return [...super.getFiles(), this.fileUrl].filter(Boolean);
  }
}

export class LabelerAssets extends BaseProjectListItemsLoader<LabelerAsset> {

  constructor(projectId: string, readonly datasetId: string, readonly importId: string) {
    super(projectId);
  }

  protected projectDataBasePath(): string {
    return `labeler/assets/${this.datasetId}/${this.importId}`;
  }

  protected deserializeItem(value: any): LabelerAsset {
    return JSON_OBJECT.deserializeObject(value, LabelerAsset);
  }

  protected serializeItem(item: LabelerAsset): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: LabelerAsset, item2: LabelerAsset): number {
    return item1.getSortIndex() - item2.getSortIndex();
  }
}

export enum LabelerReviewStatus {
  PENDING = "pending",
  REJECTED = "rejected",
  ACCEPTED = "accepted",
}

export const LABELER_REVIEW_STATUS_KTS = new KeyTextStrings([
  $KTS(LabelerReviewStatus.PENDING, "Pending"),
  $KTS(LabelerReviewStatus.REJECTED, "Rejected"),
  $KTS(LabelerReviewStatus.ACCEPTED, "Accepted"),
]);

@JsonObject()
export class LabelerActivityLog extends BaseProjectListItem {

  @JsonProperty()
  projectId: string;

  @JsonProperty()
  datasetId: string;

  @JsonProperty()
  taskId: string;

  @JsonProperty()
  submittedAt: number;

  @JsonProperty()
  reviewStatus: LabelerReviewStatus;

  @JsonProperty()
  comments: string;

  static createNew(projectId: string): LabelerActivityLog {
    const item = new LabelerActivityLog(md5_uuid());
    item.projectId = projectId;
    return item;
  }
}

export class LabelerActivityLogs extends BaseProjectListItemsLoader<LabelerActivityLog> {

  constructor(projectId: string) {
    super(projectId);
  }

  protected projectDataBasePath(): string {
    return "labeler/activityLogs";
  }

  protected deserializeItem(value: any): LabelerActivityLog {
    return JSON_OBJECT.deserializeObject(value, LabelerActivityLog);
  }

  protected serializeItem(item: LabelerActivityLog): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: LabelerActivityLog, item2: LabelerActivityLog): number {
    return item2.submittedAt - item1.submittedAt;
  }
}

export enum LabelerTeamMemberRole {
  ADMIN = "admin",
  LABELER = "labeler",
}

@JsonObject()
export class LabelerTeamMember extends BaseProjectListItem {

  @FormGen({
    name: "Email",
    type: "email",
    noneditable: true,
  })
  @JsonProperty()
  readonly email: string;

  @FormGen({
    name: "Firstname",
    type: "string",
    noneditable: true,
  })
  @JsonProperty()
  readonly firstname: string;

  @FormGen({
    name: "Lastname",
    type: "string",
    noneditable: true,
  })
  @JsonProperty()
  readonly lastname: string;

  @JsonProperty()
  readonly role: LabelerTeamMemberRole;

  // NOTE: We should not auto load assigned tasks here to avoid circular dep, as loading a task loads its assigned members as well.
  private tasks: LabelerTask[];

  async getOrLoadAssignedTasks(): Promise<LabelerTask[]> {
    if (this.tasks !== undefined) {
      return this.tasks;
    }
    const tasks: LabelerTask[] = [];
    const assignments = await ResourceData.project().labeler.assignments.loadAssignmentsByTeamMemberId(this.id);
    for (const assignment of assignments) {
      tasks.push(await new LabelerTasks(ResourceData.projectId(), assignment.datasetId).loadListItem(assignment.taskId));
    }
    this.tasks = tasks;
    return tasks;
  }

  getAssignedTasks(): LabelerTask[] | undefined {
    return this.tasks;
  }
}

export class LabelerTeamMembers extends BaseProjectListItemsLoader<LabelerTeamMember> {

  constructor(projectId: string) {
    super(projectId);
  }

  protected projectDataBasePath(): string {
    return "labeler/teamMembers";
  }

  protected deserializeItem(value: any): LabelerTeamMember {
    return JSON_OBJECT.deserializeObject(value, LabelerTeamMember);
  }

  protected serializeItem(item: LabelerTeamMember): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: LabelerTeamMember, item2: LabelerTeamMember): number {
    return item1.createdAt - item2.createdAt;
  }
}

export const teamMemberAutocompleteLoader = () => new Promise<KeyTextString[]>((resolve, reject) => {
  const teamMembers = ResourceData.project().labeler.teamMembers;
  teamMembers.getOrLoadListItems()
    .then(members => resolve(members.map(member => $KTS(member.id, DisplayName(member.firstname, member.lastname)))))
    .catch(reason => reject(reason));
});

@JsonObject()
export class LabelerAssignment extends BaseProjectListItem {

  static genId(datasetId: string, taskId: string): string {
    return md5(datasetId, taskId);
  }

  @JsonProperty()
  readonly datasetId: string;

  @JsonProperty()
  readonly taskId: string;

  @FormGen({
    name: "Assign to",
    type: "autocomplete",
    autocompleteLoader: teamMemberAutocompleteLoader,
  })
  @JsonProperty()
  readonly teamMemberId: string;

  static createNew(datasetId: string, taskId: string): LabelerAssignment {
    return new LabelerAssignment(datasetId, taskId);
  }

  constructor(datasetId: string, taskId: string) {
    super(LabelerAssignment.genId(datasetId, taskId));
    this.datasetId = datasetId;
    this.taskId = taskId;
  }

  getRels(): Rel[] {
    return [
      this.teamMemberRel(),
    ].filter(Boolean);
  }

  static readonly TEAM_MEMBERS_REL_NAME = "teamMembers";

  teamMemberRel(): Rel {
    return this.teamMemberId ? new Rel(LabelerAssignment.TEAM_MEMBERS_REL_NAME, this.teamMemberId, this.id, true, true) : null;
  }
}

export class LabelerAssignments extends BaseProjectListItemsLoader<LabelerAssignment> {

  constructor(projectId: string) {
    super(projectId);
  }

  protected projectDataBasePath(): string {
    return "labeler/assignments";
  }

  async loadAssignmentsByTeamMemberId(teamMemberId: string): Promise<LabelerAssignment[] | null> {
    const val = await this.getProvisioningContext().dbRef_getVal(this.listPath() + Rel.oneToOnePathFor(LabelerAssignment.TEAM_MEMBERS_REL_NAME, teamMemberId));
    const assignments: LabelerAssignment[] = [];
    if (val) {
      for (const id of Object.keys(val)) {
        assignments.push(await super.loadListItem(id));
      }
    }
    return assignments;
  }

  protected deserializeItem(value: any): LabelerAssignment {
    return JSON_OBJECT.deserializeObject(value, LabelerAssignment);
  }

  protected serializeItem(item: LabelerAssignment): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: LabelerAssignment, item2: LabelerAssignment): number {
    return item2.createdAt - item1.createdAt;
  }
}

/// Posts

export enum PostType {
  NEWS = "news",
  BLOG = "blog",
  LEARN = "learn",
}

export type PostTypeMetadata = {
  text: string,
  iconType: typeof SvgIcon,
  subPath: string,
  categories: KeyTextStrings,
}

export const POST_TYPE_KTM = new KeyMetadatas<PostTypeMetadata>([
  $KTM(PostType.NEWS, {
    text: "News",
    iconType: NewspaperOutlined,
    subPath: "news",
    categories: KeyTextStrings.fromList([
      "Announcement",
      "Product",
    ]),
  }),
  $KTM(PostType.BLOG, {
    text: "Blog",
    iconType: HistoryEduOutlined,
    subPath: "blog",
    categories: KeyTextStrings.fromList([
      "Article",
      "Culture",
      "Engineering",
    ]),
  }),
  $KTM(PostType.LEARN, {
    text: "Learn",
    iconType: SchoolOutlined,
    subPath: "learn",
    categories: KeyTextStrings.fromList([
      "Dental Care",
      "Conditions",
      "Procedures",
      "Health & Diet",
      "Parents & Children",
    ]),
  }),
]);

export enum PostStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
  ARCHIVED = "archived",
}

export const POST_STATUS_KTS = new KeyTextStrings([
  $KTS(PostStatus.DRAFT, "Draft"),
  $KTS(PostStatus.PUBLISHED, "Published"),
  $KTS(PostStatus.ARCHIVED, "Archived"),
]);

@JsonObject()
export class Post extends BaseListItem {

  @JsonProperty()
  readonly type: PostType;

  @FormGen({
    name: "Status",
    type: "enum",
    enumVariant: "button",
    enumValues: POST_STATUS_KTS.values,
    enumDefaultKey: PostStatus.DRAFT,
  })
  @JsonProperty()
  status: PostStatus = PostStatus.DRAFT;

  @FormGen({name: "Published At", type: "datenum"})
  @JsonProperty()
  publishedAt: number;

  @FormGen({
    name: "Category",
    type: "enum",
    _asyncInit: async function (metadata, target) {
      const type = (target as Post)?.type;
      metadata.enumValues = POST_TYPE_KTM.findMetadata(type)?.categories.values || [];
    },
  })
  @JsonProperty()
  category: string;

  @FormGen({name: "Title", type: "string", required: true})
  @JsonProperty()
  title: string;

  @FormGen({name: "Subtitle", type: "string"})
  @JsonProperty()
  subtitle: string;

  @FormGen({
    name: "Cover image",
    type: "file",
    fileMimeTypes: ["image/png", "image/jpg"],
    fileStoredDataSource: "posts/cover_images"
  })
  @JsonProperty()
  coverImageUrl: string;

  @FormGen({name: "Body", type: "text"})
  @JsonProperty()
  body: string;

  @FormGen({name: "Attachments", type: "array_file", fileMimeTypes: ["image/png", "image/jpg"]})
  @JsonProperty()
  attachments: string[];

  static createNewNews(): Post {
    return new Post(md5_uuid(), PostType.NEWS);
  }

  static createNewBlog(): Post {
    return new Post(md5_uuid(), PostType.BLOG);
  }

  static createNewLearn(): Post {
    return new Post(md5_uuid(), PostType.LEARN);
  }

  constructor(id: string, type: PostType) {
    super(id);
    this.type = type;
  }

  categoryText(): string {
    return POST_TYPE_KTM.findMetadata(this.type)?.categories.findText(this.category);
  }

  toSlug(): string {
    return StringUtil.toSlug([this.title, this.id.substring(0, 4)].join(" "));
  }

  static readonly SLUG_REL_NAME = "slugs";

  slugRel(): Rel {
    const id1 = this.toSlug();
    if (!id1) {
      return null;
    }
    return new Rel(Post.SLUG_REL_NAME, id1, this.id, false, true);
  }
}

export class Posts extends BaseListItemsLoader<Post> {

  private static readonly instances = new Map<PostType, Posts>();

  static getInstance(postType: PostType): Posts {
    let instance = this.instances.get(postType);
    if (!instance) {
      instance = new Posts(postType);
      this.instances.set(postType, instance);
    }
    return instance;
  }

  private constructor(private readonly postType: PostType) {
    super({shared: true});
  }

  protected basePath(): string {
    return `posts/${POST_TYPE_KTM.findMetadata(this.postType)?.subPath || "uncategorized"}`;
  }

  async addPost(post: Post): Promise<void> {
    await this.addListItem(post, post.slugRel());
  }

  protected deserializeItem(value: any): Post {
    return JSON_OBJECT.deserializeObject(value, Post);
  }

  protected serializeItem(item: Post): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Post, item2: Post): number {
    return item2.createdAt - item1.createdAt;
  }
}